import { Observable, ReplaySubject } from 'rxjs';
import { GeoInfo } from './geo-info.model';
import { DeviceInfo } from './device-info.model';
import { ViewSession } from './view-session.model';
import { ViewSessionEvent } from './view-session-event.model';

export class Viewer {

	public uid: string;

	private _totalViewTime: number; // in seconds
	private _initialViewTime: number; // in seconds
	private viewSessions: ViewSession[];

	private _activeSession: ViewSession;
	private _latLong: google.maps.LatLng;
	private _firstSighting: Date;
	private _lastSighting: Date;
	private _deviceInfo: DeviceInfo;
	private _geoInfo: GeoInfo;
	private _hadRemove: boolean;
	private _isRemoved: boolean;
	private _pinned: boolean;
	private _marker: google.maps.Marker;
	private _playStateChanged: ReplaySubject<string>;
	private _playState: string;
	private _publication: string;
	private _reActivated: boolean; // when a previously removed viewer gets a new 'add' event
	private _justAdded: boolean;
	private _fromBacklog: boolean;
	private _clipsWatched: number;
	private _adsWatched: number;

	constructor(ev: ViewSessionEvent) {
		this.viewSessions = [];
		this.uid = '';
		this._totalViewTime = 0;
		this._initialViewTime = 0;
		this._clipsWatched = 0;
		this._adsWatched = 0;
		this._isRemoved = false;
		this._hadRemove = false;
		this._reActivated = false;
		this._playStateChanged = new ReplaySubject<string>();
		this.update(ev); // update will come from the viewsession!
	}

	get adsWatched(): number {
		return this._adsWatched;
	}

	get clipsWatched(): number {
		return this._clipsWatched;
	}

	set fromBacklog(val) {
		this._fromBacklog = val;
	}

	get fromBacklog(): boolean {
		return this._fromBacklog;
	}

	set justAdded(val) {
		this._justAdded = val;
	}

	get justAdded(): boolean {
		return this._justAdded;
	}

	set reActivated(val) {
		this._reActivated = val;
	}

	get reActivated(): boolean {
		return this._reActivated;
	}

	get pinned(): boolean {
		return this._pinned;
	}
	set pinned(val) {
		this._pinned = val;
	}

	get publication(): string {
		return this._publication;
	}

	set hadRemove(val) {
		this._hadRemove = val;
	}

	get hadRemove(): boolean {
		return this._hadRemove;
	}

	get isRemoved(): boolean {
		return this._isRemoved;
	}

	get latLong(): google.maps.LatLng {
		return this._latLong;
	}

	get marker(): google.maps.Marker {
		return this._marker;
	}
	set marker(val: google.maps.Marker) {
		if (val != null && val.getPosition() != null) {
			this._marker = val;
		}
	}

	get playState(): string {
		return this._playState;
	}

	get playStateChanged(): Observable<string> {
		return this._playStateChanged.asObservable();
	}


	get deviceInfo(): DeviceInfo {
		return this._deviceInfo;
	}

	get isMobile(): boolean {
		return this._deviceInfo && (this._deviceInfo.deviceType_str === 'Smartphone' || this._deviceInfo.mobileTablet_bool);
	}

	get geoInfo(): GeoInfo {
		return this._geoInfo;
	}

	get activeSession(): ViewSession {
		return this._activeSession;
	}

	get firstSighting(): Date {
		return this._firstSighting;
	}

	get lastSighting(): Date {
		return this._lastSighting;
	}

	public update(ev: ViewSessionEvent) {
		if (ev) {
			if (ev.uid) {
				this.uid = ev.uid;
			}
			if (ev.fromBacklog) {
				this._fromBacklog = true;
			}
			const t: any = ev.lastUpdated || ev.timestamp || ev.time || ev.date;
			if (t && !(this._playState === 'paused' && ev.ev === 'pg')) { // don't update last sighting with paused pg events
				const ls = new Date(t);
				if (!isNaN(ls.getTime())) {
					// avoid future times:
					this._lastSighting = ls.getTime() > Date.now() ? new Date() : ls;
				}
			}
			if (ev.geo) {
				this._geoInfo = (ev.geo as GeoInfo);
				if (ev.geo.latitude && ev.geo.longitude) {
					this._latLong = new google.maps.LatLng(ev.geo.latitude, ev.geo.longitude);
				}
			}
			if (ev.pp) {
				this._publication = ev.pp;
			}
			if (ev.deviceInfo) {
				this._deviceInfo = ev.deviceInfo;
			}
			if (!this._firstSighting) {
				// updates timeAgo
				// this._firstSighting = new Date(ev.timestamp);
			}
			if (ev.action === 'remove') {
				this._isRemoved = true;
			}
			if (ev.action === 'add' || ev.ev === 'st') {
				this._isRemoved = false;
			}

			if (ev.action === 'add') {
				if (ev.viewedClips && Object.keys(ev.viewedClips).length > 1) {
					this._clipsWatched = this.viewSessions.length + (Object.keys(ev.viewedClips).length);
					Object.keys(ev.viewedClips).forEach((k: any) => {
						if (ev.viewedClips[k] && ev.viewedClips[k].to) {
							this._initialViewTime += Number(ev.viewedClips[k].to) / 1000;
						}
					});
				} else if (this._clipsWatched === 0) {
					this._clipsWatched = 1;
				}

				if (ev.viewedCommercials && Object.keys(ev.viewedCommercials)) {
					this._adsWatched = Object.keys(ev.viewedCommercials).length;
				}
			}

			if (ev.timestamp) {
				const eventTime = new Date(ev.timestamp);
				if (!this._firstSighting || this._firstSighting > eventTime) {
					this._firstSighting = eventTime;
				}
			}

			if (ev.ev === 'xst' && ev.et === 'Creative') {
				this._adsWatched++;
			}

			this._updateViewTime();
			this._updateFirstSighting();
		}

		if (this._activeSession) {
			if (this._playState) {
				if (this._playState !== this._activeSession.playState) {
					this._playStateChanged.next(this._activeSession.playState);
				}
			}
			this._playState = this._activeSession.playState;
		}
	}

	public addViewSession(viewSession: ViewSession): void {
		this.viewSessions.unshift(viewSession);
		if (this.viewSessions.length > 1) {
			this._clipsWatched++;
		}

		viewSession.eventAdded.subscribe((ev) => this.update(ev));

		// setTimeout( () => {
		if (this.viewSessions && this.viewSessions.length) {
			this.viewSessions.sort((a: ViewSession, b: ViewSession) =>
				// sort to show newest sessions first
				(new Date(b.startTime)).getTime() - (new Date(a.startTime)).getTime()
			);

			const firstViewSession = this.viewSessions[this.viewSessions.length - 1];

			if (firstViewSession && firstViewSession.startTime) {
				const fs = new Date(firstViewSession.startTime);
				if (!isNaN(fs.getTime())) {
					this._firstSighting = fs;
				}
			}
		}
		// }, 1);

		this._activeSession = this.viewSessions[0];
	}

	get totalViewTime(): number {
		return this._totalViewTime;
	}

	private _updateFirstSighting() {
		const tvtMS = this._totalViewTime * 1000;
		const vtStart = (this._lastSighting ? this._lastSighting.getTime() : Date.now()) - tvtMS;
		if (this._totalViewTime && (!this._firstSighting || vtStart < this._firstSighting.getTime())) {
			this._firstSighting = new Date(vtStart);
		}
	}

	private _updateViewTime() {
		this._totalViewTime = this._initialViewTime;
		this.viewSessions.forEach((vs: ViewSession) => {
			if (vs.viewTime) {
				this._totalViewTime += vs.viewTime;
			}
		});
	}
}
